import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { container } from "tsyringe";
import { AnalyticsService } from "../../services/analytics";

export enum ImageFormat {
  jpg,
  png,
  webp,
  avif,
}

export interface ImageConverterState {
  targetFormat: ImageFormat;
  selectedImage: File | undefined;
  selectedImageName: string;
  maxResolutionHeight: number;
  maxResolutionWidth: number;
  imageAspectRatio: number;
}

const initialState: ImageConverterState = {
  targetFormat: ImageFormat.png,
  selectedImage: undefined,
  selectedImageName: "",
  maxResolutionHeight: 0,
  maxResolutionWidth: 0,
  imageAspectRatio: 0,
};

export const ImageConverterSlice = createSlice({
  name: "imageConverter",
  initialState,
  reducers: {
    targetFormatChanged: (state, action: PayloadAction<ImageFormat>) => {
      state.targetFormat = action.payload;
    },
    maxResolutionHeightOnChanged(state, action: PayloadAction<string>) {
      try {
        var maxHeight = Number(action.payload);
        if (Number.isNaN(maxHeight)) {
          return;
        }
        state.maxResolutionHeight = maxHeight;
        state.maxResolutionWidth = Math.ceil(
          maxHeight / state.imageAspectRatio
        );
      } catch (error) {}
    },
    maxResolutionWidthOnChanged(state, action: PayloadAction<string>) {
      try {
        var maxWidth = Number(action.payload);
        if (Number.isNaN(maxWidth)) {
          return;
        }
        state.maxResolutionWidth = maxWidth;
        state.maxResolutionHeight = Math.ceil(
          maxWidth * state.imageAspectRatio
        );
      } catch (error) {}
    },
    clearImage: (state) => {
      state.selectedImage = undefined;
      state.selectedImageName = ""; 
      state.imageAspectRatio = 0;
      state.maxResolutionHeight = 0;
      state.maxResolutionWidth = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addImageAsync.fulfilled, (state, action) => {
      if (action.payload === undefined) {
        return;
      }

      if (state.selectedImage != undefined) {
        state.selectedImage = undefined;
      }

      state.selectedImage = action.payload.file;
      state.selectedImageName = action.payload.name;
      state.maxResolutionHeight = action.payload.height;
      state.maxResolutionWidth = action.payload.width;
      state.imageAspectRatio = action.payload.height / action.payload.width;
    });
  },
});

export const addImageAsync = createAsyncThunk(
  "imageConverter/addImageAsync",
  async (file: File | undefined) => {
    const analyticsService = container.resolve(AnalyticsService);
    analyticsService.LogEvent({
      category: "Button Click",
      action: "Convert Clicked",
      label: "Image Converter",
    });
    if (file === undefined) return;

    const { width, height } = await getImageDimensions(file);

    return {
      name: file.name,
      file: file,
      width: width,
      height: height,
    };
  }
);

export const convertImageAsync = createAsyncThunk<
  void,
  void,
  { state: RootState }
>("imageConverter/convertImageAsync", async (_, { getState }) => {
  const state = getState();

  const imageConvertModule = await import("browser-image-compression");
  var browserImageCoverter = imageConvertModule.default;

  var selectedFormat = ImageFormat[state.imageConverter.targetFormat];

  if (state.imageConverter.selectedImage === undefined) return;

  var originalImageWidth = (
    await getImageDimensions(state.imageConverter.selectedImage)
  ).width;
  var maxWidthOrHeight = undefined;

  if (originalImageWidth !== state.imageConverter.maxResolutionWidth) {
    maxWidthOrHeight =
      state.imageConverter.maxResolutionWidth >
      state.imageConverter.maxResolutionHeight
        ? state.imageConverter.maxResolutionWidth
        : state.imageConverter.maxResolutionHeight;
  }

  const options = {
    fileType: `image/${selectedFormat}`,
    initialQuality: 1.0,
    maxWidthOrHeight: maxWidthOrHeight,
  };

  try {
    const convertedImageBlob = await browserImageCoverter(
      state.imageConverter.selectedImage,
      options
    );

    const url = window.URL.createObjectURL(convertedImageBlob);
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.href = url;
    a.download = `${
      state.imageConverter.selectedImageName.split(".")[0]
    }.${selectedFormat}`;
    a.click();
  } catch (error) {
    console.error("Conversion failed:", error);
  }
});

export const {
  maxResolutionHeightOnChanged,
  maxResolutionWidthOnChanged,
  targetFormatChanged,
  clearImage,
} = ImageConverterSlice.actions;

export const selectState = (state: RootState) => state.imageConverter;

export default ImageConverterSlice.reducer;

async function getImageDimensions(file: File) {
  let img = new Image();
  img.src = URL.createObjectURL(file);
  await img.decode();
  let width = img.width;
  let height = img.height;
  return {
    width,
    height,
  };
}
